import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';

const navItems = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'skills', label: 'Skills' },
  { id: 'experience', label: 'Experience' },
  { id: 'projects', label: 'Projects' },
  { id: 'contact', label: 'Contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);

          const scrollPos = window.scrollY + 150;
          for (let i = navItems.length - 1; i >= 0; i--) {
            const section = document.getElementById(navItems[i].id);
            if (section && section.offsetTop <= scrollPos) {
              setActiveSection(navItems[i].id);
              break;
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  }, []);

  return (
    <NavContainer scrolled={scrolled}>
      <NavInner>
        <Logo onClick={() => scrollToSection('home')}>
          <LogoIcon>H</LogoIcon>
          <LogoText>imanshu</LogoText>
        </Logo>

        <DesktopNav>
          {navItems.map(item => (
            <NavLink
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              $active={activeSection === item.id}
            >
              {item.label}
              {activeSection === item.id && (
                <ActiveDot
                  as={motion.div}
                  layoutId="navDot"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </NavLink>
          ))}
        </DesktopNav>

        <ResumeBtn
          href="https://drive.google.com/file/d/12UGKSdczT0cU5esCnFPjF2tUOlkenmaX/view?usp=sharing"
          target="_blank"
          rel="noopener noreferrer"
        >
          Resume
        </ResumeBtn>

        <MobileToggle onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileToggle>
      </NavInner>

      <AnimatePresence>
        {isOpen && (
          <MobileMenu
            as={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {navItems.map((item, i) => (
              <MobileNavLink
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                $active={activeSection === item.id}
              >
                <span className="num">0{i + 1}.</span>
                {item.label}
              </MobileNavLink>
            ))}
            <MobileResumeBtn
              href="https://drive.google.com/file/d/1K4TY2gSQwM-oWlglL3mXXkHjQwdiezzq/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
            >
              Resume
            </MobileResumeBtn>
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavContainer>
  );
};

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.scrolled
    ? 'rgba(3, 0, 20, 0.92)'
    : 'transparent'};
  border-bottom: ${props => props.scrolled
    ? '1px solid rgba(99, 102, 241, 0.1)'
    : '1px solid transparent'};
  transition: background 0.3s ease, border-color 0.3s ease;
  will-change: background;
`;

const NavInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 72px;
`;

const Logo = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const LogoIcon = styled.span`
  font-size: 1.8rem;
  font-weight: 900;
  background: ${props => props.theme.colors.gradientPrimary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: ${props => props.theme.fonts.heading};
`;

const LogoText = styled.span`
  font-size: 1.4rem;
  font-weight: 300;
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.heading};
  letter-spacing: -0.5px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    display: none;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const NavLink = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.$active
    ? props.theme.colors.text
    : props.theme.colors.textSecondary};
  transition: color 0.2s ease;
  padding: 0.5rem 0;
  font-family: ${props => props.theme.fonts.body};

  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const ActiveDot = styled.div`
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  box-shadow: 0 0 10px ${props => props.theme.colors.glow};
`;

const ResumeBtn = styled.a`
  padding: 8px 20px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  border: 1.5px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.mono};
  letter-spacing: 0.5px;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  text-decoration: none;

  &:hover {
    background: rgba(99, 102, 241, 0.1);
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  font-size: 1.4rem;
  cursor: pointer;
  padding: 8px;
  z-index: 1001;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: flex;
    align-items: center;
  }
`;

const MobileMenu = styled.div`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 1.5rem 2rem 2rem;
    background: rgba(3, 0, 20, 0.97);
    border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  }
`;

const MobileNavLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.$active
    ? props.theme.colors.primary
    : props.theme.colors.textSecondary};
  padding: 0.8rem 1.5rem;
  width: 100%;
  text-align: left;
  border-radius: 8px;
  transition: color 0.2s ease;
  font-family: ${props => props.theme.fonts.body};

  .num {
    color: ${props => props.theme.colors.primary};
    font-family: ${props => props.theme.fonts.mono};
    font-size: 0.8rem;
    margin-right: 0.8rem;
  }

  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const MobileResumeBtn = styled.a`
  display: inline-block;
  margin-top: 0.5rem;
  padding: 10px 28px;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  border: 1.5px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  text-decoration: none;
  font-family: ${props => props.theme.fonts.mono};
  text-align: center;
`;


export default React.memo(Navbar);
